import * as React from "react";
import { AvatarImage } from "@/components/ui/avatar";

interface SafeAvatarImageProps extends React.ComponentPropsWithoutRef<typeof AvatarImage> {
  src: string;
}

/**
 * A wrapper around AvatarImage that preloads the image URL.
 * If the image fails to load (expired CDN token, CORS, 404, etc.),
 * it renders nothing — letting Radix's AvatarFallback display instead.
 */
export const SafeAvatarImage: React.FC<SafeAvatarImageProps> = ({ src, ...props }) => {
  const [status, setStatus] = React.useState<"loading" | "success" | "error">("loading");

  React.useEffect(() => {
    if (!src || !src.trim()) {
      setStatus("error");
      return;
    }

    setStatus("loading");
    const img = new Image();
    img.onload = () => setStatus("success");
    img.onerror = () => setStatus("error");
    img.src = src;

    if (img.complete) {
      if (img.naturalWidth > 0) {
        setStatus("success");
      } else {
        setStatus("error");
      }
    }

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  if (status !== "success") return null;

  return <AvatarImage src={src} {...props} />;
};
